// @ts-nocheck

import { intersection, difference, isEqual } from 'lodash';

import { LocalStorageMissionsKeys } from '../modules/missions/constants/storage';
import BetBuilder from '../states/betbuilder';
import { StreamingActionTypes, CommonActionTypes } from '../types/actions/common';
import { FavoriteActionTypes } from '../types/actions/favorite';
import { UserActionTypes } from '../types/actions/user';
import RoutesRedirectionFactory from '../utils/routes/RoutesRedirectionFactory';

export const entitiesInitialState = {
  // That come from the backend
  matches: {},
  bets: {},
  betCategories: {},
  outcomes: {},
  odds: {},
  favorites: {},
  filters: {},
  sports: {},
  categories: {},
  tournaments: {},
  players: {},
  calendar: {},
  teasers: [],
  sportIds: [],
  home: {},
  live: {},
  multi: {},
  betslipIds: null,
  betslips: {},
  reallyLiveMatchCount: 0,
  // Internal
  streams: {},
  // grids
  grids: {},
  pools: {},
  missions: {},
  achievements: {},
  competitors: {},
};

// this is used to know if we have to update entity (and no overwriting)
const entityPrimaryKey = {
  matches: 'matchId',
  bets: 'betId',
  outcomes: ['outcomeId', 'oddId'],
  sports: 'sportId',
  categories: 'categoryId',
  tournaments: 'tournamentId',
  players: 'playerId',
  betslips: 'betslipId',
  calendar: 'calendarId',
  filters: 'betFilterId',
  pools: 'poolId',
  betCategories: 'betCategoryId',
  missions: 'missionId',
  achievements: 'achievementId',
  competitors: 'competitorId',
};

const DEBUG = false;

const allowedKeys = Object.keys(entitiesInitialState);

// const avoidEntities = ['debug', 'browser', 'ccOk', 'winamaxTV', 'nbBetslips'];
// const specialEntities = ['sportIds', 'home', 'live', 'tv', 'favorites', 'teasers', 'grids', 'betslipIds'];

export default (history) =>
  (state = entitiesInitialState, action, { basket, betbuilder }) => {
    // Reset on user disconnect
    if (action.type === UserActionTypes.SET_DISCONNECTED) {
      return {
        ...state,
        betslipIds: null,
        betslips: {},
        streams: {},
      };
    }
    if (action.type === StreamingActionTypes.GET_STREAMINGURL_OK) {
      const { response, matchId } = action.payload;
      const { estr, ecode, u } = response;
      if (ecode === 0) {
        const url = u;
        return {
          ...state,
          streams: {
            ...state.streams,
            [matchId]: {
              isExist: true,
              isAllowed: true,
              url,
            },
          },
        };
      }
      return {
        ...state,
        streams: {
          ...state.streams,
          [matchId]: {
            isAllowed: false,
            ecode,
            estr,
          },
        },
      };
    }

    if (action.type === CommonActionTypes.RECEIVE_ENTITIES) {
      const { location } = history ?? {};
      const { pathname, hash } = location ?? {};

      const entities = action.payload;

      // At first, shallowly copy the old state
      const newState = { ...state };

      // Entities that could not be deleted
      const requirements = {
        matches: [],
        bets: [],
        outcomes: [],
      };

      // Aggregate basket requirements
      for (const id in basket.items) {
        const { matchId, oddId, betId, betbuilder, selectionItems } = basket.items[id];
        if (requirements['matches'].indexOf(matchId) === -1) {
          requirements['matches'].push(matchId);
        }
        // Special case for betbuilder item
        if (betbuilder) {
          for (let x = 0; x < selectionItems.length; x++) {
            if (requirements['bets'].indexOf(selectionItems[x].betId) === -1) {
              requirements['bets'].push(selectionItems[x].betId);
            }
            if (BetBuilder.isMultichoiceItem(selectionItems[x])) {
              const { outcomeIds } = selectionItems[x];
              for (let y = 0; y < outcomeIds.length; y++) {
                if (requirements['outcomes'].indexOf(outcomeIds[y]) === -1) {
                  requirements['outcomes'].push(outcomeIds[y]);
                }
              }
            } else {
              if (requirements['outcomes'].indexOf(selectionItems[x].outcomeId) === -1) {
                requirements['outcomes'].push(selectionItems[x].outcomeId);
              }
            }
          }
        } else {
          if (requirements['bets'].indexOf(betId) === -1) {
            requirements['bets'].push(betId);
          }
          if (requirements['outcomes'].indexOf(oddId) === -1) {
            requirements['outcomes'].push(oddId);
          }
        }
      }
      // Aggregate betbuilder requirements
      for (const id in betbuilder.matches) {
        const {
          selection: { items },
        } = betbuilder.matches[id];
        if (requirements['matches'].indexOf(id) === -1) {
          requirements['matches'].push(parseInt(id, 10));
        }
        for (let x = 0; x < items.length; x++) {
          if (requirements['bets'].indexOf(items[x].betId) === -1) {
            requirements['bets'].push(items[x].betId);
          }
          if (BetBuilder.isMultichoiceItem(items[x])) {
            const { outcomeIds } = items[x];
            for (let y = 0; y < outcomeIds.length; y++) {
              if (requirements['outcomes'].indexOf(outcomeIds[y]) === -1) {
                requirements['outcomes'].push(outcomeIds[y]);
              }
            }
          } else {
            if (requirements['outcomes'].indexOf(items[x].outcomeId) === -1) {
              requirements['outcomes'].push(items[x].outcomeId);
            }
          }
        }
      }

      const entityTypesToUpdate = Object.keys(entities).filter((key) => allowedKeys.indexOf(key) !== -1);

      for (let x = 0; x < entityTypesToUpdate.length; x++) {
        const entityType = entityTypesToUpdate[x];

        // Regular types
        let hasChanged = false;
        DEBUG &&
          console.time(
            `Scripting time for updating ${entityType} (${Object.keys(entities?.[entityType] || []).length})`,
          );
        DEBUG && console.groupCollapsed(`Update for key ${entityType}`);
        if (
          typeof entities[entityType] === 'object' &&
          entities[entityType] !== null &&
          !Array.isArray(entities[entityType])
        ) {
          // Should be an object
          const ids = Object.keys(entities[entityType]);
          for (let y = 0; y < ids.length; y++) {
            // The update to merge with the current entity
            let id = ids[y];
            // it's a number and not a betbuilder odds and not a grid
            if (!isNaN(parseInt(id, 10)) && id.indexOf('U') === -1 && id.indexOf('-') === -1) {
              id = parseInt(id, 10);
            }
            const entity = entities[entityType][id];

            if (entity !== null) {
              const currentEntity = newState[entityType][id];
              // If its not a type like matches, bets, etc... Something like odds, home, live, tv
              if (Object.keys(entityPrimaryKey).indexOf(entityType) === -1) {
                // entities[entityType] looks like this
                // id stand for matches, topTvMatches keys
                // { multi: { matches: [...], topTvMatches: [...] } }
                if (currentEntity !== entity) {
                  // Special case for odds, we deal with value instead of object
                  DEBUG && console.group(`Update odd ${id}`);
                  DEBUG && console.log(`value: ${currentEntity} => ${entity}`);
                  DEBUG && console.groupEnd();
                  if (!hasChanged) {
                    hasChanged = true;
                    newState[entityType] = { ...newState[entityType] };
                  }
                  newState[entityType][id] = entity;
                }
              } else {
                // entities[entityType] looks like this
                // id stand for the entity identifier
                // { matches: { [id]: {...} } }
                if (currentEntity && !currentEntity['@@invalid']) {
                  // The entity already exists
                  let shouldUpdateEntity = false;
                  const existingProperty = intersection(Object.keys(currentEntity), Object.keys(entity));
                  const newProperty = difference(Object.keys(entity), Object.keys(currentEntity));
                  shouldUpdateEntity =
                    newProperty.length > 0 ||
                    existingProperty.reduce((shouldUpdate, key) => {
                      if (key !== entityPrimaryKey[entityType]) {
                        return shouldUpdate || !isEqual(currentEntity[key], entity[key]);
                      }
                      return shouldUpdate;
                    }, shouldUpdateEntity);
                  if (DEBUG) {
                    console.group(`Update key ${id}`);
                    for (let i = 0; i < existingProperty.length; i++) {
                      const key = existingProperty[i];
                      if (key !== entityPrimaryKey[entityType]) {
                        console.log(`update property ${key}: ${currentEntity[key]} => ${entity[key]}`);
                      }
                    }
                    for (let i = 0; i < newProperty.length; i++) {
                      const key = newProperty[i];
                      if (key !== entityPrimaryKey[entityType]) {
                        console.log(`new property ${key}: ${currentEntity[key]} => ${entity[key]}`);
                      }
                    }
                    console.log(`Should update: ${shouldUpdateEntity}`);
                    console.groupEnd();
                  }
                  if (shouldUpdateEntity) {
                    if (!hasChanged) {
                      hasChanged = true;
                      newState[entityType] = { ...newState[entityType] };
                    }
                    newState[entityType][id] = {
                      ...newState[entityType][id],
                      ...entity,
                    };
                  }
                } else {
                  // New entity to append to the state
                  if (!hasChanged) {
                    hasChanged = true;
                    newState[entityType] = { ...newState[entityType] };
                  }
                  if (entityPrimaryKey[entityType]) {
                    if (Array.isArray(entityPrimaryKey[entityType])) {
                      newState[entityType][id] = entityPrimaryKey[entityType].reduce(
                        (result, primaryKey) => {
                          result[primaryKey] = parseInt(id, 10);
                          return result;
                        },
                        { ...entity },
                      );
                    } else {
                      newState[entityType][id] = {
                        [entityPrimaryKey[entityType]]: parseInt(id, 10),
                        ...entity,
                      };
                    }
                  } else {
                    newState[entityType][id] = entity;
                  }
                }
              }
            } else {
              if (!hasChanged) {
                hasChanged = true;
                newState[entityType] = { ...newState[entityType] };
              }
              if (!requirements[entityType]) {
                requirements[entityType] = [];
              }
              if (requirements[entityType].indexOf(id) !== -1 && newState[entityType][id]) {
                newState[entityType][id] = {
                  ...newState[entityType][id],
                  [`@@invalid`]: true,
                };
              } else {
                newState[entityType][id] = null;
              }
            }
          }
        } else {
          // entities[entities] should be one of the following: array, boolean, string, number, undefined, null
          // Seul l'entité betslipIds peut revenir a une valeure null
          if (entities[entityType] !== null || entityType === 'betslipIds') {
            newState[entityType] = entities[entityType];
          }
        }

        DEBUG && console.groupEnd();
        DEBUG &&
          console.timeEnd(
            `Scripting time for updating ${entityType} (${Object.keys(entities?.[entityType] || []).length})`,
          );
      }

      if (
        (pathname && pathname.indexOf(RoutesRedirectionFactory.getExtrasRoutePathname()) !== -1) ||
        (hash && hash.indexOf('mission') !== -1)
      ) {
        localStorage.setItem(LocalStorageMissionsKeys.MISSIONS, JSON.stringify(newState.missions ?? {}));
      }

      if (
        (pathname && pathname.indexOf(RoutesRedirectionFactory.getExtrasRoutePathname()) !== -1) ||
        (hash && hash.indexOf('mission') !== -1)
      ) {
        localStorage.setItem(LocalStorageMissionsKeys.ACHIEVEMENTS, JSON.stringify(newState.achievements ?? {}));
      }

      return newState;
    }

    if (action.type === FavoriteActionTypes.PURGE) {
      return {
        ...state,
        favorites: {
          matchCount: 0,
          liveMatchCount: 0,
          tvMatchCount: 0,
          matches: [],
        },
      };
    }

    return state;
  };
